(ns minorodata.components.log.table
  (:require [lucide-react :refer [ChevronRight]]
            [minorodata.app.handlers :refer [dispatch]]
            [minorodata.app.posthog-events :as posthog-events]
            [minorodata.components.ace-editor.core :as ace]
            [minorodata.components.log.controls :as log.controls]
            [minorodata.components.spinner.core :as spinner]
            [re-posh.core :as re-posh]
            [reagent.core :as reagent]
            [reitit.frontend.easy :as rt.easy]
            [ui.button :as button]
            [ui.table :refer [sort-label table tbody td th thead tr]]
            [ui.utils :refer [clsx]]
            [ui.date-time :refer [date-tooltip]]))


(defn row-chevron [{:keys [in]}]
  [:div {:class (clsx "text-kleene-secondary-v2 transition-transform"
                      {"rotate-90" in})
         :style {:line-height 0}
         :title "Open details"}
   [:> ChevronRight {}]])


(defn message-formatter [message-map default-message]
  (let [{:keys [message error event transform-name model-name]} message-map
        {:keys [kleene-helper]} error
        event-names {"starting-task"  "Starting task"
                     "launched-task"  "Launching task"
                     "completed-task" "Completed task"
                     "skipped-task"   "Skipped because an upstream transform failed"}]
    (cond

      kleene-helper
      kleene-helper

      error
      error

      message
      message

      (and (or transform-name model-name) event)
      (get event-names event event)

      :else default-message)))


(defn pill [{:keys [ellipsis] :as props} & children]
  (let [tag (if (:href props) :a :div)]
    (into [tag (-> props
                   (dissoc :ellipsis)
                   (update :class #(clsx "inline-flex gap-2 items-center px-3 py-1.5 transition-colors cursor-pointer rounded-full leading-4 pill"
                                         {"max-w-full overflow-hidden" ellipsis}
                                         %)))]
          (if ellipsis
            [[:span {:class "whitespace-nowrap text-ellipsis overflow-hidden max-w-full"} children]]
            children))))

(defn underline [{:keys [ellipsis] :as props} & children]
  (let [tag (if (:href props) :a :div)]
    (into [tag (-> props
                   (dissoc :ellipsis)
                   (update :class #(clsx "inline-flex gap-2 items-center hover:text-kleene dark:hover:text-kleene hover:underline transition-colors cursor-pointer leading-4"
                                         {"max-w-full overflow-hidden" ellipsis}
                                         %)))]
          (if ellipsis
            [[:span {:class "whitespace-nowrap text-ellipsis overflow-hidden max-w-full"} children]]
            children))))

(defn expanded-row [{:keys [message-map message extract-context]}]
  (let [loading? @(re-posh/subscribe [:mold-http/loading? :transform.api/fetch-single-transform])]
    [:div {:class "flex flex-col rounded-b-lg bg-[#282a36]"}
     [ace/editor
      {:read-only         true
       :show-print-margin false
       :value             message
       :height            "180px"}]
     [:div {:class "p-2 flex flex-row gap-2"}
      (when (and (:source-type extract-context) (:extract-name extract-context))
        [button/button {:variant :empty
                        :href (rt.easy/href :log {} {:source (:source-type extract-context)
                                                     :term   (:extract-name extract-context)})}
         "Go to Logs"])
      (cond
        (and (:extract-id message-map) (:source-id message-map) (not extract-context))
        [button/button {:variant :empty
                        :href (rt.easy/href :extract-details (select-keys message-map [:extract-id :source-id]))}
         "Open Extract"]
        (:transform-id message-map)
        [:<>
         [button/button {:variant :empty
                         :href (rt.easy/href :transform-details {:transform-id (:transform-id message-map)
                                                                 :group-id     (:transform-group-id message-map)})}
          "Open transform group"]
         [button/button {:variant :empty
                         :disabled loading?
                         :class (when loading? "cursor-progress")
                         :on-click (fn []
                                     (dispatch [:transform.api/fetch-single-transform
                                                {:transform-id (:transform-id message-map)
                                                 :group-id (:transform-group-id message-map)
                                                 :on-finish #(dispatch [:sql-console-modal.events/open-transform (:transform-id message-map)])}]))}
          "Open in SQL console"]]
        (:model-id message-map)
        [button/button {:variant :empty
                        :href (rt.easy/href :analytics-model-instance-details
                                            {:instance-id (:model-id message-map)})}
         "Open model"]
        :else nil)]]))

(def message-type-color
  {"INFO"    "bg-kleene-secondary-v2"
   "SUCCESS" "bg-kleene-primary-01"
   "FAILURE" "bg-kleene-error-01"})

(def message-type-name
  {"INFO"    "Info"
   "SUCCESS" "Success"
   "FAILURE" "Failure"})

(defn type-indicator
  [type]
  [:div {:class (clsx "w-2 h-2 rounded-full"
                      (get message-type-color type))}])

(defn type-pill
  [{:keys [type on-click class]}]
  [pill {:class    (clsx {"hover:bg-kleene-neutral-04 dark:hover:bg-kleene-dark-04" on-click}
                         class)
         :title    (str "Filter by type: " type)
         :on-click on-click}
   [type-indicator type]
   (get message-type-name type)])

(defn source-pill
  [{:keys [source-id source-name]}]
  [underline {:ellipsis true
              :class    ""
              :title    (str "Filter by source: " source-name)
              :on-click (fn [evt]
                          (when source-id
                            (.stopPropagation evt)
                            (log.controls/apply-logs-search {:logs-view/source source-id} true)))}
   source-name])

(defn name-pill
  [{:keys [transform-name source-id]}]
  [underline {:ellipsis true
              :class    ""
              :title    (str "Filter by name: " transform-name)
              :on-click (fn [evt]
                          (when transform-name
                            (.stopPropagation evt)
                            (log.controls/apply-logs-search {:logs-view/source source-id
                                                             :logs-view/term   transform-name}
                                                            true)))}
   transform-name])

(defn task-pill
  [{:keys [id-trimmed job-id]}]
  [underline {:class    "whitespace-nowrap font-mono"
              :title    (str "Filter by task id: " id-trimmed)
              :on-click (fn [evt]
                          (when job-id
                            (.stopPropagation evt)
                            (log.controls/apply-logs-search {:logs-view/term job-id}
                                                            true)))}
   id-trimmed])

(defn logs-row [{:keys [message-id skip-columns interactive extract-context]
                 :or {interactive true}}]
  (let [opened? (reagent/atom false)
        message-atom (re-posh/subscribe [:logs.subs/log-message message-id])
        route-name (re-posh/subscribe [:app-route/prop :app-route/name])
        column-count (cond-> 4
                       (not (some #(= % "source-name") skip-columns)) inc
                       (not (some #(= % "name") skip-columns)) inc)]
    (fn []
      (let [viewer-opeened? @opened?
            {:keys [type source-name source-id extr-transform-name
                    date message message-map job-id job-id-trimmed
                    task-execution-id task-execution-id-trimmed]}
            @message-atom]
        [:<>
         [tr {:class    (clsx "cursor-pointer h-10 transition-colors hover:bg-kleene-neutral-05 dark:hover:bg-kleene-dark-03"
                              {"has-[.pill:hover]:bg-transparent dark:has-[.pill:hover]:bg-transparent" interactive})
              :on-click (fn []
                          (when (and (not @opened?) (= :log @route-name))
                            (dispatch [:posthog/capture {:event (:logs/details-viewed posthog-events/events)
                                                         :props {:log-type type}}]))
                          (swap! opened? not))}
          [td {:class "min-w-[210px] max-w-[210px] whitespace-nowrap font-medium border-r px-1 py-1"}
           [:div {:class "flex flex-row gap-1 items-center"}
            [row-chevron {:in viewer-opeened?}]
            [date-tooltip {:date date
                           :seconds? true}]]]

          [td {:class "min-w-[90px] border-r px-1 py-1"}
           [type-pill {:type        type
                       :on-click    (when interactive
                                      (fn [ev]
                                        (.stopPropagation ev)
                                        (log.controls/apply-logs-search {:logs-view/type type} true)))}]]

          (when-not (some #(= % "source-name") skip-columns)
            [td {:class "min-w-[140px] max-w-[180px] border-r px-2 py-1"
                 :title source-name}
             (when source-name
               [source-pill {:source-id source-id
                             :source-name source-name}])])

          (when-not (some #(= % "name") skip-columns)
            [td {:class    "min-w-[140px] max-w-[250px] border-r px-2 py-1"
                 :title    extr-transform-name}
             (when extr-transform-name
               [name-pill {:transform-name extr-transform-name
                           :source-id      source-id}])])

          [td {:class    "min-w-[150px] max-w-[600px] border-r px-2 py-1"
               :ellipsis true}
           (message-formatter message-map message)]

          [td {:class    "min-w-[120px] max-w-[120px] px-2 py-1"
               :title    (if (= source-id "job-manager")
                           task-execution-id
                           job-id)}
           [task-pill {:id-trimmed (if (= source-id "job-manager")
                                     task-execution-id-trimmed
                                     job-id-trimmed)
                       :job-id (if (= source-id "job-manager")
                                 task-execution-id
                                 job-id)}]]]

         (when viewer-opeened?
           [tr
            [td {:colSpan column-count :no-padding true}
             [expanded-row {:message-map     message-map
                            :message         message
                            :extract-context extract-context}]]])]))))


(defn sort-action [prev-sort sort order]
  (fn []
    (dispatch [:logs-view/update
               {:logs-view/sort-by    sort
                :logs-view/sort-order (cond
                                        (not= prev-sort sort) "asc"
                                        (= order "asc") "desc"
                                        :else "asc")}])
    (dispatch [:logs.api/fetch])))


(defn logs-table-head []
  (let [{:logs-view/keys [sort-by sort-order]} @(re-posh/subscribe [:logs-view/pull])
        order    (or sort-order "asc")
        timezone @(re-posh/subscribe [:app.subs/timezone])]
    [thead
     [tr {:sticky true
          :class  "*:top-[61px]! 2xl:*:top-[81px]!"}
      [th {:style {:width "210px" :minWidth "210px" :height "52px"}
           :class "border-r"}
       [sort-label {:active    (= sort-by :created_at)
                    :direction order
                    :on-click  (sort-action sort-by :created_at order)}
        [:span
         "Date"
         [:div {:class "-mt-1"}
          [:span {:class "text-xs text-kleene-dark-05 dark:text-kleene-dark-03"} timezone]]]]]
      [th {:style {:width "80px"}
           :class "border-r"}
       "Type"]
      [th {:style {:width "180px"}
           :class "border-r"}
       "Source"]
      [th {:style {:width "80px"}
           :class "border-r"}
       "Name"]
      [th {:class "border-r"}
       "Message"]
      [th {:style {:width "80px"}} "Task"]]]))


(defn log-rows []
  (let [messages @(re-posh/subscribe [:logs-pager/prop :logs-pager/messages])]
    [tbody {}
     (for [message-id messages]
       ^{:key message-id}
       [logs-row {:message-id message-id}])]))


(defn logs-spinner []
  (let [loading-logs?       @(re-posh/subscribe [:mold-http/loading? :logs.api/fetch])
        loading-sources?    @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-sources])
        loading-connectors? @(re-posh/subscribe [:mold-http/loading? :connections.api/fetch-user-connectors])

        loading?            (or loading-logs? loading-sources? loading-connectors?)]

    [spinner/full-screen {:open    loading?
                          :classes {:backdrop "absolute! z-40! items-start! pt-40!"}}]))

(defn logs-table []
  [:div {:class "relative flex-1 px-0 h-full"}
   [logs-spinner]
   [table {:data-cy       "logs-table"
           :sticky-header true}
    [logs-table-head]
    [log-rows]]])
